import React, { useMemo, useRef } from "react";
import { Check, XCircle } from "lucide-react";
import { CI_MAIN_COUNT, CI_MAIN_INDICATORS, buildNationalCiRanking } from "../dpatScoreStats";
import DpatExportButtons from "./DpatExportButtons";
import {
  buildCiRankingHtml,
  buildCiRankingRows,
  downloadCsvFromRows,
  downloadExcelSheets,
  downloadNodeAsPdf,
  downloadWordFromHtml,
} from "../dpatDashboardExport";

function CiStatusCell({ fulfilled }) {
  if (fulfilled === true) {
    return (
      <span className="inline-flex items-center justify-center" title="Fulfilled">
        <Check
          size={18}
          strokeWidth={3}
          className="text-emerald-600"
          aria-label="Fulfilled"
        />
      </span>
    );
  }
  if (fulfilled === false) {
    return (
      <span className="inline-flex items-center justify-center" title="Not Fulfilled">
        <XCircle
          size={18}
          strokeWidth={2.4}
          className="text-red-600"
          aria-label="Not Fulfilled"
        />
      </span>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}

function lookupByKey(list, key) {
  return (list || []).find((item) => item.key === key);
}

const DpatCiRankingTable = ({
  rows,
  loading,
  reportingYear,
  tableCaptureRef,
  scoreSource = "final",
  onScoreSourceChange,
}) => {
  const localRef = useRef(null);
  const captureRef = tableCaptureRef || localRef;
  const sortedRows = useMemo(() => buildNationalCiRanking(rows), [rows]);
  const scoreLabel =
    scoreSource === "system"
      ? "System Score"
      : scoreSource === "technical"
        ? "Technical Team Score"
        : "Final Outcome";

  const colSpan = 4 + CI_MAIN_INDICATORS.length;

  const sourceBtnClass = (active) =>
    `rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
      active
        ? "bg-[#16325a] text-white shadow-sm"
        : "bg-transparent text-muted-foreground hover:bg-[#16325a]/10 hover:text-[#16325a]"
    }`;

  const handleTablePdf = async () => {
    if (!captureRef.current) return;
    await downloadNodeAsPdf(captureRef.current, `dpat-ci-ranking-${reportingYear}.pdf`);
  };

  const handleTableWord = () => {
    downloadWordFromHtml(
      buildCiRankingHtml(sortedRows, reportingYear),
      `dpat-ci-ranking-${reportingYear}.doc`,
    );
  };

  const handleTableExcel = () => {
    downloadExcelSheets(
      [{ name: "CI ranking", rows: buildCiRankingRows(sortedRows) }],
      `dpat-ci-ranking-${reportingYear}.xlsx`,
    );
  };

  const handleTableCsv = () => {
    downloadCsvFromRows(buildCiRankingRows(sortedRows), `dpat-ci-ranking-${reportingYear}.csv`);
  };

  return (
    <section
      ref={captureRef}
      data-pdf-part="ci-ranking"
      className="rounded-xl border border-border bg-card shadow-sm"
    >
      <div className="border-b border-border px-4 py-4 md:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground md:text-xl">
              National Compliance Indicators (CI) Ranking
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {scoreLabel} — total CI out of {CI_MAIN_COUNT}, plus each main indicator. Ranked by
              Total CI (4/4 first). Green check = Fulfilled, red × = Not Fulfilled.
            </p>
          </div>
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            {onScoreSourceChange ? (
              <div data-export-hide className="flex rounded-lg border border-border bg-muted/40 p-0.5">
                <button
                  type="button"
                  className={sourceBtnClass(scoreSource === "final")}
                  onClick={() => onScoreSourceChange("final")}
                >
                  Final Outcome
                </button>
                <button
                  type="button"
                  className={sourceBtnClass(scoreSource === "technical")}
                  onClick={() => onScoreSourceChange("technical")}
                >
                  Technical Team Score
                </button>
                <button
                  type="button"
                  className={sourceBtnClass(scoreSource === "system")}
                  onClick={() => onScoreSourceChange("system")}
                >
                  System Score
                </button>
              </div>
            ) : null}
            <DpatExportButtons
            label="Download table"
            onPdf={handleTablePdf}
            onWord={handleTableWord}
            onExcel={handleTableExcel}
            onCsv={handleTableCsv}
            disabled={loading || sortedRows.length === 0}
          />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-semibold md:px-6">Rank</th>
              <th className="px-4 py-3 font-semibold md:px-6">District</th>
              <th className="px-4 py-3 font-semibold md:px-6">Region</th>
              <th className="px-4 py-3 font-semibold md:px-6">Total CI</th>
              {CI_MAIN_INDICATORS.map((indicator) => (
                <th
                  key={indicator.key}
                  className="px-4 py-3 font-semibold normal-case tracking-normal md:px-6"
                  title={indicator.thematicArea}
                >
                  <span className="block text-xs uppercase tracking-wide">{indicator.code}</span>
                  <span className="mt-0.5 block text-[11px] font-normal leading-tight text-muted-foreground">
                    {indicator.thematicArea}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-8 text-center text-muted-foreground md:px-6">
                  Loading CI ranking…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-8 text-center text-muted-foreground md:px-6">
                  No districts match the current filters.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => (
                <tr key={row.districtId} className="border-b border-border/60 hover:bg-muted/20">
                  <td className="px-4 py-3 font-bold tabular-nums text-foreground md:px-6">
                    {row.rank != null ? row.rank : "—"}
                  </td>
                  <td className="px-4 py-3 font-medium text-foreground md:px-6">{row.districtName}</td>
                  <td className="px-4 py-3 text-muted-foreground md:px-6">{row.regionName}</td>
                  <td className="px-4 py-3 font-semibold tabular-nums md:px-6">
                    {row.hasData
                      ? `${row.complianceFulfilled ?? 0} / ${row.complianceTotal || CI_MAIN_COUNT}`
                      : "—"}
                  </td>
                  {CI_MAIN_INDICATORS.map((indicator) => (
                    <td key={indicator.key} className="px-4 py-3 text-center md:px-6">
                      <CiStatusCell
                        fulfilled={lookupByKey(row.ciIndicators, indicator.key)?.fulfilled}
                      />
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default DpatCiRankingTable;
